"use client";

/**
 * `PercentField` — numeric input that visually presents itself as a
 * percent control via a `%` suffix label.
 *
 * STORAGE CONVENTION: the value is stored as the **raw number entered**.
 * When a user types `15`, the form-state value is `15` (not `0.15`).
 * Downstream zod schemas decide what that number means — usually
 * `z.number().min(0).max(100)` for a 0-100 percent input. This is
 * intentionally simpler than the historic "store as decimal, multiply on
 * change, divide on display" pattern: there is no impedance mismatch
 * between what the user sees in the input and what is committed to form
 * state, no rounding artefacts on round-trip, and zod's range validators
 * read naturally.
 *
 * Display formatting (e.g. "20,00 %" in Serbian locale) is the caller's
 * job — the surrounding {@link Form} composer routes through its
 * `formatters` bundle for read-only displays. Inside the input the user
 * sees the canonical numeric representation that the browser renders for
 * `<input type="number">`.
 *
 * If a future caller really needs decimal storage (0.15) for parity with
 * a backend convention, they can wrap the field in a `.transform()` on
 * the zod schema rather than the engine carrying both conventions.
 *
 * Visual layout follows the same pattern as {@link MoneyField}: input on
 * the left, suffix span on the right, joined into a single visual unit.
 */

import * as React from "react";
import { Input } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { PercentFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface PercentFieldProps {
  field: PercentFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function PercentField({
  field,
  pathPrefix,
}: PercentFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <div className="flex items-stretch gap-0">
        <Input
          id={path}
          type="number"
          step="0.01"
          inputMode="decimal"
          aria-invalid={errorMessage !== undefined ? true : undefined}
          className="rounded-r-none"
          {...(field.disabled ? { disabled: true } : {})}
          {...(field.placeholder !== undefined
            ? { placeholder: field.placeholder }
            : {})}
          {...form.register(path, { valueAsNumber: true })}
        />
        <span
          aria-hidden="true"
          className="inline-flex h-[var(--aio-input-height,2.75rem)] items-center justify-center rounded-r-md border border-l-0 border-border bg-muted px-3 text-sm text-fg-muted"
        >
          %
        </span>
      </div>
    </FieldShell>
  );
}
